import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import ADNavbar from "../components/ADNavbar";
import { siteConfig, books, quickLinks, projectStatus } from "../data/mock";
import { ArrowRight, ExternalLink } from "lucide-react";

/* ── Hero ── */
const Hero = () => (
  <section className="pt-36 pb-28 px-6">
    <div className="max-w-4xl mx-auto text-center">
      <p className="text-[11px] tracking-[0.35em] uppercase mb-10" style={{ color: "#999" }}>
        AUTHOR &bull; STRATEGIST &bull; OPERATOR
      </p>
      <h1
        className="text-3xl md:text-5xl lg:text-[3.5rem] font-bold text-gray-900 leading-[1.15] mb-8"
        style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.01em" }}
      >
        Entrepreneur. Sales Leader.<br />Builder of Top Producers.
      </h1>
      <div className="w-10 h-px mx-auto mb-8" style={{ backgroundColor: "#d1d1d1" }} />
      <p className="text-base md:text-lg leading-relaxed mx-auto" style={{ color: "#777", maxWidth: "600px" }}>
        Proven systems that create high-income earners and scalable business results.
      </p>
    </div>
  </section>
);

/* ── Portrait ── */
const Portrait = () => (
  <section className="pb-16 px-6">
    <div className="max-w-xs mx-auto">
      <div className="border border-gray-100 p-1.5">
        <img
          src="https://customer-assets.emergentagent.com/job_wanderlust-club/artifacts/usgmm81i_image.png"
          alt="Adris Davis"
          className="w-full h-auto"
        />
      </div>
    </div>
  </section>
);

/* ── Section 1: What I Do ── */
const WhatIDo = () => (
  <section className="py-20 px-6" style={{ backgroundColor: "#FAFAFA" }}>
    <div className="max-w-5xl mx-auto">
      <h2
        className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-14"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        What I Do
      </h2>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { title: "Author", text: "Publishing frameworks on credit, business growth, and elite sales performance." },
          { title: "Entrepreneur", text: "Building and scaling businesses focused on financial empowerment and real-world results." },
          { title: "Mentor", text: "Developing top-producing sales professionals through proven systems and accountability." },
        ].map((card, i) => (
          <div
            key={i}
            className="bg-white border border-gray-100 p-8 text-center transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
          >
            <h3
              className="text-base font-bold text-gray-900 mb-3"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {card.title}
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed">{card.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ── Section 2: Proven Results ── */
const ProvenResults = () => (
  <section className="py-20 px-6">
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-col lg:flex-row gap-12 items-center">
        {/* Content */}
        <div className="lg:w-7/12">
          <h2
            className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Proven Results
          </h2>
          <p className="text-sm text-gray-400 italic mb-10">
            This isn't theory. This is production.
          </p>
          <div className="space-y-4">
            {[
              "Six-figure earners developed",
              "Million-dollar producers mentored",
              "Consistent top performers across multiple industries",
              "20+ years of real-world sales and leadership experience",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 py-3 border-b border-gray-100 last:border-0">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                <p className="text-sm text-gray-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Watch image — blended off to the side */}
        <div className="lg:w-5/12 hidden lg:block">
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_wanderlust-club/artifacts/8kuoj9ux_image.png"
              alt="On the move"
              className="w-full h-auto rounded-sm"
              style={{ opacity: 0.85 }}
            />
            <div className="absolute inset-0 rounded-sm" style={{ background: "linear-gradient(to left, transparent 60%, rgba(255,255,255,1) 100%)" }} />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── Lifestyle Divider ── */
const LifestyleDivider = () => null;

/* ── Section 3: Published Works ── */
const PublishedWorks = () => (
  <section className="py-20 px-6" style={{ backgroundColor: "#FAFAFA" }}>
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-14">
        <h2
          className="text-2xl md:text-3xl font-bold text-gray-900"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Published Works
        </h2>
        <Link to="/books" className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors">
          View all <ArrowRight size={12} />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {books.map((book) => (
          <a key={book.id} href={book.amazonUrl} target="_blank" rel="noopener noreferrer" className="group block">
            <div className="bg-white border border-gray-100 overflow-hidden transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-md">
              <div className="h-52 bg-gray-50 flex items-center justify-center">
                <img src={book.cover} alt={book.title} className="w-full h-full object-contain p-2" loading="lazy" />
              </div>
              <div className="p-5">
                <h3
                  className="text-sm font-semibold text-gray-900 mb-1 leading-snug"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {book.title}
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed mb-4">
                  {book.description.slice(0, 80)}...
                </p>
                <span className="text-[11px] text-gray-400 group-hover:text-gray-600 transition-colors flex items-center gap-1">
                  View Details <ExternalLink size={10} />
                </span>
              </div>
            </div>
          </a>
        ))}
        {/* 6th slot placeholder */}
        <div className="border border-dashed border-gray-200 flex items-center justify-center min-h-[280px]">
          <span className="text-xs text-gray-300 tracking-wider uppercase">More coming</span>
        </div>
      </div>
    </div>
  </section>
);

/* ── Section 4: Team Hustle ── */
const CoachingMentorship = () => (
  <section className="py-20 px-6">
    <div className="max-w-5xl mx-auto">
      <h2
        className="text-2xl md:text-3xl font-bold text-gray-900 mb-12"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        Coaching & Mentorship
      </h2>
      <div className="flex flex-col lg:flex-row gap-12">
        <div className="lg:w-1/2">
          <p className="text-sm text-gray-500 leading-[1.9] mb-6">
            Team Hustle is where top producers are built. This is production-driven mentorship designed to create high-income earners and elite performers.
          </p>
          <p className="text-sm text-gray-400 leading-[1.9]">
            With over two decades in high-pressure sales environments, Adris Davis has developed a system rooted in real-world execution—not theory, not recycled training programs.
          </p>
        </div>
        <div className="lg:w-1/2">
          <div className="space-y-3 mb-8">
            {[
              "Closing mastery",
              "Buyer psychology",
              "Objection control",
              "Daily production systems",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                <p className="text-sm text-gray-600">{item}</p>
              </div>
            ))}
          </div>
          <Link
            to="/initiatives/team-hustle"
            className="text-sm text-gray-900 border border-gray-900 px-8 py-2.5 tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all duration-300 inline-block"
          >
            Apply for Mentorship
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* ── Section 5: Social Impact ── */
const SocialImpact = () => (
  <section className="py-20 px-6" style={{ backgroundColor: "#FAFAFA" }}>
    <div className="max-w-3xl mx-auto">
      <h2
        className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        Projects & Impact
      </h2>
      <p className="text-sm text-gray-500 leading-[1.9] mb-8">
        A platform created to bring visibility to missing persons cases when traditional alerts fall short. Social Media Amber Alert uses the speed and reach of social platforms to help bridge that gap—sharing cases, increasing visibility, and mobilizing people to stay aware.
      </p>
      <Link
        to="/initiatives/amber-alert"
        className="text-sm text-gray-900 border border-gray-900 px-8 py-2.5 tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all duration-300 inline-block"
      >
        View Project
      </Link>
    </div>
  </section>
);

/* ── Section 6: Final CTA ── */
const FinalCTA = () => (
  <section className="py-24 px-6" style={{ backgroundColor: "#111" }}>
    <div className="max-w-3xl mx-auto text-center">
      <h2
        className="text-2xl md:text-3xl font-bold text-white mb-5"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        Ready to Operate at a Higher Level?
      </h2>
      <p className="text-sm leading-relaxed mb-10" style={{ color: "#777", maxWidth: "500px", margin: "0 auto 2.5rem" }}>
        Whether you're building a business or scaling your income, the next level requires a different approach.
      </p>
      <Link
        to="/books"
        className="text-sm text-white border border-white/30 px-10 py-3 tracking-wider uppercase hover:bg-white hover:text-gray-900 transition-all duration-300 inline-block"
      >
        Explore My Work
      </Link>
    </div>
  </section>
);

/* ── Footer ── */
const Footer = () => (
  <footer className="py-16 px-6" style={{ backgroundColor: "#0B1628" }}>
    <div className="max-w-4xl mx-auto text-center">
      <p className="text-sm text-white/40 mb-8">
        © {new Date().getFullYear()} AdrisDavis.com
      </p>
      <p className="text-xs text-white/25">
        A Prestigious Companies Network | Strategic Partner of the Prestigious Travel Club Foundation
      </p>
    </div>
  </footer>
);

/* ── Admin Widget ── */
const ProjectStatusWidget = () => (
  <section className="py-10 px-6" style={{ backgroundColor: "#F5F5F5" }}>
    <div className="max-w-2xl mx-auto">
      <div className="border border-gray-200 bg-white p-6">
        <p className="text-xs tracking-[0.2em] uppercase text-gray-400 mb-4">Project Status</p>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Next Release:</span>
            <span className="text-sm text-gray-700">{projectStatus.nextBookRelease}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Current Focus:</span>
            <span className="text-sm text-gray-700">{projectStatus.currentFocus}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── Page ── */
const ADHomePage = () => {
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  return (
    <>
      <ADNavbar />
      <Hero />
      <Portrait />
      <WhatIDo />
      <ProvenResults />
      <LifestyleDivider />
      <PublishedWorks />
      <CoachingMentorship />
      <SocialImpact />
      <FinalCTA />
      {isAdmin && <ProjectStatusWidget />}
      <Footer />
    </>
  );
};

export default ADHomePage;
